import {
    getAllocationGroupRoute,
    getAllocationGroupStatusColor,
    getAllocationGroupStatusLabel,
} from '@/components/AllocationSelectionFilter/utils';
import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types';
import { AllocationVariantOverrideVisibility } from '@/components/AllocationSelectionPanel/AllocationVariantVisibilityOverride.vue';
import { BadgeVariant } from '@/components/Badge/Badge.vue';
import { ACTIVE_EVENT_VARIANT_STATES } from '@/event/types';
import { ACTIVE_EXTERNAL_EVENT_VARIANT_STATES } from '@/external_event/externalEvent';
import {
    AllocationSelectionFilterEventFragment,
    AllocationSelectionFilterEventVariantFragment,
    AllocationSelectionFilterExternalEventFragment,
    AllocationSelectionFilterExternalEventVariantFragment,
    AllocationSelectionFilterProjectFragment,
    AllocationSelectionFilterProjectVariantFragment,
    ExternalEventLocationVisibility,
    GetEventsInTimeRangeQuery,
    GetExternalEventsInTimeRangeQuery,
    GetProjectsInTimeRangeQuery,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { ACTIVE_PROJECT_VARIANT_STATES } from '@/project/types';
import { insideRangeCheck } from '@/utils/date';
import { VariantStatus } from '@/variant/types';
import { getVariantRoute, getVariantStatusLabel, variantStatusToBadgeVariant } from '@/variant/utils';
import { DateTime } from 'luxon';
import { computed, Ref } from 'vue';
import { Section, VariantVisibility } from '../AllocationDesignerSelectionPanel.vue';
import { VariantState, VariantStateVisibility } from '../AllocationSelectionFilter.state';
import {
    DesignerStateSendType,
    EventFilterSendType,
    EventFilterSnapshotType,
    ExternalEventFilterSnapshotType,
    ProjectFilterSnapshotType,
} from '../allocationDesigner';

export type FilterObject =
    | AllocationSelectionFilterEventFragment
    | AllocationSelectionFilterProjectFragment
    | AllocationSelectionFilterExternalEventFragment;

export type FilterVariant =
    | AllocationSelectionFilterEventVariantFragment
    | AllocationSelectionFilterExternalEventVariantFragment
    | AllocationSelectionFilterProjectVariantFragment;

export type BaseAllocation = {
    id: string;
    start: string;
    end: string;
    locationId?: string;
};

const activeVariantStates = [
    ...new Set([
        ...ACTIVE_EVENT_VARIANT_STATES,
        ...ACTIVE_PROJECT_VARIANT_STATES,
        ...ACTIVE_EXTERNAL_EVENT_VARIANT_STATES,
    ]),
];

export const VISIBILITY_TOGGLE_OPTIONS = ['Visible', 'Invisible'];

export function updateChangedStatesOnToggle(
    variantId: string,
    toggleValue: VariantStateVisibility,
    defaultState: VariantState,
    changedStates: VariantState[],
) {
    const changedState = changedStates.find((s) => s.variantId === variantId);
    if (changedState) {
        if (defaultState.visibility === changedState.visibility) {
            return changedStates.filter((s) => s.variantId !== variantId);
        }
        return [...changedStates, { ...defaultState, visibility: toggleValue }];
    }
    return changedStates;
}

export function updateChangedStateOnVisibilityOverride(
    variantId: string,
    defaultState: VariantState,
    changedStates: VariantState[],
    visibilityOverride: VariantStateVisibility,
) {
    const changedState = changedStates.find((s) => s.variantId === variantId);
    if (changedState) {
        if (defaultState.visibilityOverride === changedState.visibilityOverride) {
            return changedStates.filter((s) => s.variantId !== variantId);
        }
        return [...changedStates, { ...defaultState, visibilityOverride }];
    }
    return changedStates;
}
function defaultVisibleStatus(activeGroupStates: string[], variantStatus: string, groupStatus: string) {
    if (activeVariantStates.includes(variantStatus) && activeGroupStates.includes(groupStatus)) {
        return 'Visible';
    }
    return 'Invisible';
}

function allocationsInRange(allocations: BaseAllocation[], start: DateTime, end: DateTime) {
    return allocations.some((allocation) => {
        const allocationStart = DateTime.fromISO(allocation.start);
        const allocationEnd = DateTime.fromISO(allocation.end);
        return insideRangeCheck(allocationStart, allocationEnd, start, end);
    });
}

function mergeAllocations(variant: FilterVariant) {
    let allocations: BaseAllocation[] = [];
    if ('rooms' in variant) {
        allocations = [...allocations, ...variant.rooms.nodes];
    }
    if ('areas' in variant) {
        allocations = [...allocations, ...variant.areas.nodes];
    }
    if ('allocations' in variant) {
        allocations = [...allocations, ...variant.allocations.nodes];
    }
    return allocations;
}

function calculateVariantVisibility(
    activeGroupStates: string[],
    variant: FilterVariant,
    group: FilterObject,
    start: DateTime,
    end: DateTime,
): VariantVisibility {
    const allocations = mergeAllocations(variant);
    if (allocations.length <= 0) {
        return 'No-Allocations';
    }
    if (!allocationsInRange(allocations, start, end)) {
        return 'Outside-Timeline-Range';
    }

    return defaultVisibleStatus(activeGroupStates, variant.status, group.status);
}

function hasInvisibleAllocations(variant: FilterVariant, group: FilterObject): boolean {
    if (!('allocations' in variant) || !('visibilities' in group)) {
        return false;
    }

    const allocations = variant.allocations.nodes;
    const visibilities = group.visibilities.nodes;
    if (allocations.length === 0 || visibilities.length === 0) {
        return false;
    }

    const visibilityByLocation = new Map(visibilities.map((v) => [v.locationId, v.visibility]));

    const slotAllInvisible = new Map<string, boolean>();

    for (const { start, end, locationId } of allocations) {
        const visibility = visibilityByLocation.get(locationId);
        if (visibility === undefined) continue;

        const key = `${start}|${end}`;
        const isInvisible = visibility === ExternalEventLocationVisibility.Invisible;
        slotAllInvisible.set(key, (slotAllInvisible.get(key) ?? true) && isInvisible);
    }

    return [...slotAllInvisible.values()].some(Boolean);
}

function mapVariant(
    activeGroupStates: string[],
    type: AllocationGroupType,
    variant: FilterVariant,
    group: FilterObject,
    start: DateTime,
    end: DateTime,
) {
    return {
        id: variant.id,
        status: variant.status,
        index: variant.index,
        title: variant.name,
        linkTarget: getVariantRoute(type, group.id, variant.index) ?? '',
        statusColor: variantStatusToBadgeVariant(type, variant.status as VariantStatus) as BadgeVariant,
        statusLabel: getVariantStatusLabel(type, variant.status as VariantStatus),
        isMainVariant: variant.isCurrent,
        visibility: calculateVariantVisibility(activeGroupStates, variant, group, start, end),
        hasInvisibleAllocations: hasInvisibleAllocations(variant, group),
    };
}

export function getVisibleIdsFromSections(sections: Section[]) {
    const sectionVisibleIds: string[] = [];
    sections.map((section) => {
        const activeVisibles = section.activeVariants.filter((v) => v.visibility === 'Visible').map((v) => v.id);
        const nonActiveVisibles = section.nonActiveVariants.filter((v) => v.visibility === 'Visible').map((v) => v.id);
        sectionVisibleIds.push(...activeVisibles);
        sectionVisibleIds.push(...nonActiveVisibles);
    });
    return sectionVisibleIds;
}

export function getOverrideVisibilityIdsFromSections(sections: Section[]) {
    const overrideIds: string[] = [];
    sections.map((section) => {
        const activeVisibles = section.activeVariants
            .filter((v) => v.overrideVisibility === 'Visible')
            .map((v) => v.id);
        const nonActiveVisibles = section.nonActiveVariants
            .filter((v) => v.overrideVisibility === 'Visible')
            .map((v) => v.id);
        overrideIds.push(...activeVisibles);
        overrideIds.push(...nonActiveVisibles);
    });
    return overrideIds;
}

export function getDefaultStatesFromSections(sections: Section[], variantType: AllocationGroupType) {
    const states: VariantState[] = [];
    sections.map((section) => {
        const activeStates = section.activeVariants
            .filter((v) => VISIBILITY_TOGGLE_OPTIONS.includes(v.visibility))
            .map((v) => ({ id: '', variantId: v.id, variantType, visibility: v.visibility as VariantStateVisibility }));
        const nonActiveStates = section.nonActiveVariants
            .filter((v) => VISIBILITY_TOGGLE_OPTIONS.includes(v.visibility))
            .map((v) => ({ id: '', variantId: v.id, variantType, visibility: v.visibility as VariantStateVisibility }));
        states.push(...activeStates);
        states.push(...nonActiveStates);
    });
    return states;
}

function getDefaultVisibilities(defaultStates: VariantState[], scenarioStates: VariantState[]) {
    if (!scenarioStates) {
        return defaultStates;
    }
    // overwrite defaultStates with ScenarioStates, if they exist
    return defaultStates.map((dState) => {
        const scenarioState = scenarioStates.find((sState) => sState.variantId === dState.variantId);
        if (scenarioState) {
            return scenarioState;
        }
        return dState;
    });
}

export function getChangedStatesFromSections(
    sections: Section[],
    variantType: AllocationGroupType,
    defaultStates: VariantState[],
    scenarioStates: VariantState[],
) {
    const changedStates = [];
    const combinedDefaultStates = getDefaultVisibilities(defaultStates, scenarioStates);
    for (const section of sections) {
        for (const variant of section.activeVariants.filter((variant) =>
            VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility),
        )) {
            const inCombinedDefaultState = combinedDefaultStates.find(
                (state) => state.variantId === variant.id && state.visibility === variant.visibility,
            );
            if (!inCombinedDefaultState) {
                const inScenarioState = scenarioStates.find((state) => state.variantId === variant.id);
                if (inScenarioState) {
                    changedStates.push({
                        ...inScenarioState,
                        visibility: variant.visibility as VariantStateVisibility,
                    });
                } else {
                    changedStates.push({
                        id: '',
                        variantId: variant.id,
                        variantType,
                        visibility: variant.visibility as VariantStateVisibility,
                    });
                }
            }
        }
        for (const variant of section.nonActiveVariants.filter((variant) =>
            VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility),
        )) {
            const inCombinedDefaultState = combinedDefaultStates.find(
                (state) => state.variantId === variant.id && state.visibility === variant.visibility,
            );
            if (!inCombinedDefaultState) {
                const inScenarioState = scenarioStates.find((state) => state.variantId === variant.id);
                if (inScenarioState) {
                    changedStates.push({
                        ...inScenarioState,
                        visibility: variant.visibility as VariantStateVisibility,
                    });
                }
                changedStates.push({
                    id: '',
                    variantId: variant.id,
                    variantType,
                    visibility: variant.visibility as VariantStateVisibility,
                });
            }
        }
    }
    return changedStates;
}

export function createSectionsFromFilterData(
    activeGroupStates: string[],
    type: AllocationGroupType,
    result: GetExternalEventsInTimeRangeQuery | GetEventsInTimeRangeQuery | GetProjectsInTimeRangeQuery | undefined,
    start: DateTime,
    end: DateTime,
): Section[] {
    if (result) {
        const allocationGroups = result.allocationGroups?.nodes;
        if (allocationGroups) {
            return allocationGroups.map((group) => {
                const series = 'series' in group ? group.series : undefined;
                return {
                    id: group.id,
                    title: group.nameShort,
                    status: group.status,
                    linkTarget: getAllocationGroupRoute(type, group.id),
                    statusColor: getAllocationGroupStatusColor(type, group.status),
                    statusLabel: getAllocationGroupStatusLabel(type, group.status),
                    isExpanded: true,
                    areNonActiveVariantsExpanded: false,
                    activeVariants: group.activeVariants.nodes.map((variant) =>
                        mapVariant(activeGroupStates, type, variant, group, start, end),
                    ),
                    nonActiveVariants: group.nonActiveVariants.nodes.map((variant) =>
                        mapVariant(activeGroupStates, type, variant, group, start, end),
                    ),
                    category: series?.category,
                    nationalString: series
                        ? series.national
                            ? $t('external-event-national-choice')
                            : $t('external-event-international-choice')
                        : undefined,
                };
            });
        }
    }
    return [];
}

export function updateSectionsVisibility(sections: Section[], visibilities: VariantState[]) {
    return sections.map((section) => {
        const updatedActiveVariants = section.activeVariants.map((variant) => {
            if (VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)) {
                const visibilityObj = visibilities.find((vis) => vis.variantId === variant.id);
                if (visibilityObj) {
                    return { ...variant, visibility: visibilityObj.visibility };
                }
                return variant;
            }
            return variant;
        });
        const updateNonActiveVariants = section.nonActiveVariants.map((variant) => {
            if (VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)) {
                const visibilityObj = visibilities.find((vis) => vis.variantId === variant.id);
                if (visibilityObj) {
                    return { ...variant, visibility: visibilityObj.visibility };
                }
                return variant;
            }
            return variant;
        });
        return { ...section, activeVariants: updatedActiveVariants, nonActiveVariants: updateNonActiveVariants };
    });
}

export function getSelectionFilterLabel(type: AllocationGroupType) {
    return $t('allocation-selection-filter-allocation-type', { allocationGroupType: type });
}

export function toggleSectionIsExpanded(sections: Section[], sectionId: string) {
    return sections.map((section) => {
        if (section.id === sectionId) {
            return { ...section, isExpanded: !section.isExpanded };
        }
        return section;
    });
}

export function updateSectionsOnToggle(
    sections: Section[],
    toggleValue: VariantStateVisibility,
    sectionId: string,
    variantId: string,
) {
    return sections.map((section) => {
        const updatedActiveVariants = section.activeVariants.map((variant) => {
            if (section.id === sectionId && variant.id === variantId) {
                return { ...variant, visibility: toggleValue };
            }
            return variant;
        });
        const updateNonActiveVariants = section.nonActiveVariants.map((variant) => {
            if (section.id === sectionId && variant.id === variantId) {
                return { ...variant, visibility: toggleValue };
            }
            return variant;
        });
        return { ...section, activeVariants: updatedActiveVariants, nonActiveVariants: updateNonActiveVariants };
    });
}

export function updateSectionsVisibilityOverride(
    sections: Section[],
    toggleValue: AllocationVariantOverrideVisibility,
    sectionId: string,
    variantId: string,
) {
    return sections.map((section) => {
        const updatedActiveVariants = section.activeVariants.map((variant) => {
            if (section.id === sectionId && variant.id === variantId) {
                return { ...variant, overrideVisibility: toggleValue };
            }
            return variant;
        });
        const updateNonActiveVariants = section.nonActiveVariants.map((variant) => {
            if (section.id === sectionId && variant.id === variantId) {
                return { ...variant, overrideVisibility: toggleValue };
            }
            return variant;
        });
        return { ...section, activeVariants: updatedActiveVariants, nonActiveVariants: updateNonActiveVariants };
    });
}

export function makeMainVariantVisible(sections: Section[], activeGroupStates: string[]) {
    const updatedSections = sections.map((section) => {
        const updatedActiveAllocations = section.activeVariants.map((variant) => {
            if (VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)) {
                if (variant.isMainVariant && activeGroupStates.includes(section.status)) {
                    return { ...variant, visibility: 'Visible' as const };
                } else {
                    return { ...variant, visibility: 'Invisible' as const };
                }
            }
            return variant;
        });
        const updateNonActiveVariants = section.nonActiveVariants.map((variant) => {
            if (VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)) {
                if (variant.isMainVariant && activeGroupStates.includes(section.status)) {
                    return { ...variant, visibility: 'Visible' as const };
                } else {
                    return { ...variant, visibility: 'Invisible' as const };
                }
            }
            return variant;
        });
        return { ...section, activeVariants: updatedActiveAllocations, nonActiveVariants: updateNonActiveVariants };
    });

    return updatedSections;
}

export function makeActiveVariantsVisible(sections: Section[], activeGroupStates: string[]) {
    const updatedSections = sections.map((section) => {
        const updateActiveVariants = section.activeVariants.map((variant) => {
            if (
                activeVariantStates.includes(variant.status) &&
                activeGroupStates.includes(section.status) &&
                VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)
            ) {
                return { ...variant, visibility: 'Visible' as const };
            }
            return variant;
        });
        const updateNonActiveVariants = section.nonActiveVariants.map((variant) => {
            if (VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)) {
                return { ...variant, visibility: 'Invisible' as const };
            }
            return variant;
        });
        return { ...section, activeVariants: updateActiveVariants, nonActiveVariants: updateNonActiveVariants };
    });
    return updatedSections;
}

export function toggleNonActiveVariants(sectionId: string, sections: Section[]) {
    return sections.map((section) => {
        if (section.id === sectionId) {
            return { ...section, areNonActiveVariantsExpanded: !section.areNonActiveVariantsExpanded };
        }
        return section;
    });
}

export function useDesignerFilterData(
    snapshot: Ref<EventFilterSnapshotType | ProjectFilterSnapshotType | ExternalEventFilterSnapshotType>,
    send: EventFilterSendType,
    designerStateSend: DesignerStateSendType,
    start: Ref<DateTime>,
    end: Ref<DateTime>,
) {
    const status = computed(() => {
        if (snapshot.value.context.changedStates.length === 0) {
            return 'No-Changes';
        }
        switch (snapshot.value.context.type) {
            case 'Event':
                return 'Changes-Events';
            case 'Project':
                return 'Changes-Projects';
            case 'ExternalEvent':
                return 'Changes-External-Events';
        }
    });

    const sections = computed(() => snapshot.value.context.sections);
    const changedStates = computed(() => snapshot.value.context.changedStates);

    const plusCount = computed(() => changedStates.value.filter((s) => s.visibility === 'Visible').length);
    const minusCount = computed(() => changedStates.value.filter((s) => s.visibility === 'Invisible').length);

    const visibleIds = computed(() => getVisibleIdsFromSections(sections.value));
    const visibleCount = computed(() => visibleIds.value.length);

    const overrideVisibilityFilterIds = computed(() => getOverrideVisibilityIdsFromSections(sections.value));

    function undoChanges() {
        send({ type: 'undo.changes', start: start.value, end: end.value });
        designerStateSend({ type: 'unset.scenario.saved' });
    }

    function setMainVariantVisible() {
        send({ type: 'make.mainvariant.visible' });
        designerStateSend({ type: 'unset.scenario.saved' });
    }

    function toggleVisibility(toggleValue: VariantStateVisibility, sectionId: string, variantId: string) {
        send({
            type: 'toggle.variant.visibility',
            toggleValue,
            sectionId,
            variantId,
        });
        designerStateSend({ type: 'unset.scenario.saved' });
    }

    function toggleOverrideVisibility(
        toggleValue: AllocationVariantOverrideVisibility,
        sectionId: string,
        variantId: string,
    ) {
        send({
            type: 'toggle.variant.visibilityOverride',
            toggleValue,
            sectionId,
            variantId,
        });
        designerStateSend({ type: 'unset.scenario.saved' });
    }

    function setActiveVariantsVisible() {
        send({ type: 'make.activevariant.visible' });
        designerStateSend({ type: 'unset.scenario.saved' });
    }

    function toggleNonActiveVariants(sectionId: string) {
        send({ type: 'toggle.nonactive.variants', sectionId });
        designerStateSend({ type: 'unset.scenario.saved' });
    }

    return {
        status,
        sections,
        visibleIds,
        overrideVisibilityFilterIds,
        visibleCount,
        plusCount,
        minusCount,
        changedStates,
        undoChanges,
        setMainVariantVisible,
        setActiveVariantsVisible,
        toggleNonActiveVariants,
        toggleVisibility,
        toggleOverrideVisibility,
    };
}
